import { API_URLS } from '../constants/config';
import { playerCardPath } from './playersApi';
import { authorizedPost } from './stadiumsApi';
import { extractEntity, extractList } from './tournamentsApi';

export const ADMIN_SEARCH_LIMIT = 20;

export const ADMIN_SEARCH_TYPES = [
  { type: 'city', field: 'city_id', labelKey: 'searchTypeCity', nameKeys: ['city_name', 'city', 'city_ru'] },
  { type: 'league', field: 'league_id', labelKey: 'searchTypeLeague', nameKeys: ['league_name', 'name'] },
  { type: 'championship', field: 'champ_id', labelKey: 'searchTypeChamp', nameKeys: ['champ_name', 'championship_name', 'name'] },
  { type: 'team', field: 'team_id', labelKey: 'searchTypeTeam', nameKeys: ['team_name', 'name'] },
  { type: 'stadium', field: 'stadium_id', labelKey: 'searchTypeStadium', nameKeys: ['stadium_name', 'name'] },
  { type: 'match', field: 'game_id', labelKey: 'searchTypeMatch', nameKeys: ['game_name', 'match_name', 'name', 'title'] },
  { type: 'player', field: 'player_id', labelKey: 'searchTypePlayer', nameKeys: ['player_name', 'name', 'family'] },
  { type: 'client', field: 'client_id', labelKey: 'searchTypeClient', nameKeys: ['client_name', 'name'] },
  { type: 'person', field: 'person_id', labelKey: 'searchTypePerson', nameKeys: ['person_name', 'name', 'family'] },
];

export const NEWS_ENTITY_TYPES = ADMIN_SEARCH_TYPES.filter(({ type }) => type !== 'client');
export const NEWS_TAG_BINDINGS = NEWS_ENTITY_TYPES;
export const GLOBAL_SEARCH_TYPES = ['player', 'team', 'league', 'stadium', 'match'];

export const searchTypeLabelKey = (type) => (
  ADMIN_SEARCH_TYPES.find((item) => item.type === normalizeSearchType(type))?.labelKey || ''
);

export const emptyEntityFilters = () => {
  const next = {};
  NEWS_ENTITY_TYPES.forEach(({ type }) => {
    next[type] = null;
  });
  return next;
};

const firstText = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const playerName = (item) => firstText(
  [item.family, item.name, item.father_name].filter(Boolean).join(' ').trim(),
  item.player_name,
  item.nickname,
  item.login
);

export const searchItemId = (item) => {
  const raw = item?.id ?? item?.entity_id ?? item?.value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const nestedEntityId = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const id = searchItemId({ id: values[i] });
    if (id) return id;
  }
  return null;
};

export const entityAdminPath = (item) => {
  const type = normalizeSearchType(item?.type);
  const id = searchItemId(item);
  if (!id || !type) return '';
  if (type === 'player') {
    return playerCardPath(id, nestedEntityId(item.team_id, item.teamId, item.team?.id));
  }
  if (type === 'team') return `/teams_management/${id}`;
  if (type === 'stadium') return `/stadiums_management/${id}`;
  if (type === 'match') return `/manage_match/${id}`;
  if (type === 'league') {
    const countryId = nestedEntityId(item.country_id, item.countryId, item.country?.id, item.county_id);
    return countryId ? `/tournaments/${countryId}/league/${id}` : '/schedule_management';
  }
  return '';
};

export const resolveEntityAdminPath = async (dispatch, item) => {
  const type = normalizeSearchType(item?.type);
  const id = searchItemId(item);
  let path = entityAdminPath(item);
  if (type !== 'league' || !id || (path && path !== '/schedule_management')) return path;
  try {
    const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id });
    const league = extractEntity(response.data);
    const countryId = nestedEntityId(league?.country_id, league?.countryId, league?.country?.id);
    if (countryId) return `/tournaments/${countryId}/league/${id}`;
  } catch (error) {
    return path;
  }
  return path;
};

export const SEARCH_TYPE_ALIASES = {
  champ: 'championship',
  championat: 'championship',
  championship: 'championship',
  game: 'match',
  match: 'match',
  official: 'person',
};

export const normalizeSearchType = (type) => (
  SEARCH_TYPE_ALIASES[type] || String(type || '').trim() || ''
);

export const fromServerSearchType = (type) => normalizeSearchType(type);

export const toServerSearchType = (type) => {
  if (type === 'all') return 'all';
  return normalizeSearchType(type) || String(type || '').trim();
};

export const searchItemName = (item, type) => {
  if (!item) return '';
  const provided = firstText(item.name, item.title, item.label);
  if (provided) return provided;
  if (type === 'player' || type === 'person') {
    const named = playerName(item);
    if (named) return named;
  }
  if (type === 'match') {
    const home = firstText(item.team1_name, item.home_name, item.team_1_name);
    const away = firstText(item.team2_name, item.away_name, item.team_2_name);
    if (home && away) return `${home} — ${away}`;
  }
  const spec = ADMIN_SEARCH_TYPES.find((entry) => entry.type === type);
  const named = firstText(...(spec?.nameKeys || []).map((key) => item[key]));
  const id = searchItemId(item);
  return named || (id ? `#${id}` : '');
};

export const searchItemSubtitle = (item, type) => {
  if (!item) return '';
  const provided = firstText(item.subtitle, item.description);
  if (provided) return provided;
  if (type === 'city') return firstText(item.city_ru, item.country, item.country_name);
  if (type === 'championship') return firstText(item.city, item.city_ru, item.city_name);
  if (type === 'league') return firstText(item.champ_name, item.season, item.city_name);
  if (type === 'team') return firstText(item.city, item.city_name, item.league_name);
  if (type === 'stadium') return firstText(item.address, item.city, item.city_name);
  if (type === 'match') return firstText(item.date, item.datetime, item.league_name, item.champ_name);
  if (type === 'player') return firstText(item.team_name, item.nickname);
  if (type === 'client') return firstText(item.login, item.email);
  if (type === 'person') return firstText(item.position, item.role_name, item.login);
  return '';
};

export const normalizeSearchItem = (item, type) => {
  const resolvedType = normalizeSearchType(item?.type || type);
  const id = searchItemId(item);
  if (!id) return null;
  return {
    ...item,
    id,
    type: resolvedType,
    name: searchItemName(item, resolvedType),
    subtitle: searchItemSubtitle(item, resolvedType),
    image: item.image || item.logo || item.photo || '',
  };
};

const listFromGroup = (group) => (
  Array.isArray(group?.items) ? group.items : (Array.isArray(group) ? group : [])
);

export const extractSearchItems = (data, type) => {
  const groups = {};
  if (data?.groups && typeof data.groups === 'object' && !Array.isArray(data.groups)) {
    Object.keys(data.groups).forEach((groupType) => {
      const resolvedType = normalizeSearchType(groupType);
      groups[resolvedType] = listFromGroup(data.groups[groupType])
        .map((item) => normalizeSearchItem(item, resolvedType))
        .filter(Boolean);
    });
  }

  const rawList = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : extractList(data);
  const items = (rawList.length
    ? rawList.map((item) => normalizeSearchItem(item, type))
    : Object.keys(groups).reduce((all, groupType) => all.concat(groups[groupType]), [])
  ).filter(Boolean);

  const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
  return { items, groups, total };
};

export const adminSearch = async (dispatch, {
  type,
  types,
  search = '',
  limit = ADMIN_SEARCH_LIMIT,
  offset = 0,
  champId,
}) => {
  const body = { limit, offset };
  if (Array.isArray(types) && types.length) body.types = types;
  else if (type) body.type = type;
  if (search && String(search).trim()) body.search = String(search).trim();
  if (champId) body.champ_id = Number(champId);
  const response = await authorizedPost(dispatch, API_URLS.getAdminSearch, body);
  return extractSearchItems(response.data, Array.isArray(type) ? type[0] : type);
};

export const entityFiltersToQuery = (filters = {}) => {
  const query = {};
  NEWS_ENTITY_TYPES.forEach(({ type, field }) => {
    const id = Number(filters[type]?.id);
    if (Number.isFinite(id) && id > 0) query[field] = id;
  });
  return query;
};

export const hasEntityFilters = (filters) => Object.keys(entityFiltersToQuery(filters)).length > 0;

export const bindingLabel = (tag, t) => {
  const parts = [];
  NEWS_ENTITY_TYPES.forEach(({ type, labelKey }) => {
    const item = tag?.bindings?.[type];
    if (item?.id) parts.push(`${t(labelKey)}: ${item.name || `#${item.id}`}`);
  });
  return parts.join(' · ');
};
